import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FarmerBreederCoverPage = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  const saveToMarketing = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/marketing/ads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: 'P.O.S - Farmer & Breeder Services',
          platform: 'Point of Sale Material',
          type: 'Printable PDF - A4',
          image_url: '/farmer-cover',
          notes: 'One-page branded service overview for farmers and breeders. Print to PDF from browser.',
        }),
      });
      if (response.ok) {
        setSaved(true);
        alert('✅ Saved to Admin Marketing → P.O.S Materials!');
      }
    } catch (error) {
      alert('Error saving. Please try again.');
    }
    setSaving(false);
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-page { 
            margin: 0; 
            padding: 0;
            box-shadow: none !important;
          }
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
      `}</style>

      {/* Control Bar - Hidden on Print */}
      <div className="no-print bg-gray-100 p-4 border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="text-[#1a4d2e] hover:underline"
          >
            ← Back to Home
          </button>
          <div className="flex items-center gap-4">
            <span className="text-gray-600 text-sm">Farmer & Breeder Services</span>
            <button 
              onClick={saveToMarketing}
              disabled={saving || saved}
              className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 ${
                saved ? 'bg-green-500 text-white' : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
            >
              {saved ? '✓ Saved to P.O.S' : saving ? 'Saving...' : '💾 Save to Marketing'}
            </button>
            <button 
              onClick={handlePrint}
              className="bg-[#1a4d2e] text-white px-6 py-2 rounded-lg hover:bg-[#2d5a3d] font-semibold flex items-center gap-2"
            >
              <span>🖨️</span> Print / Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* PDF Document - A4 Size */}
      <div className="flex justify-center p-8 no-print">
        <div 
          className="print-page bg-white shadow-2xl"
          style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}
        >
          <DocumentContent />
        </div>
      </div>

      {/* Print Version */}
      <div className="hidden print:block">
        <DocumentContent />
      </div>
    </>
  );
};

const DocumentContent = () => (
  <div className="h-full flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
    
    {/* Header */}
    <div className="bg-[#1a4d2e] text-white px-8 py-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">MEASURED LIVESTOCK</h1>
          <p className="text-white/70 text-sm mt-1">Professional On-Farm Services</p>
        </div>
        <div className="text-right">
          <p className="text-white/80 text-xs">measuredlivestock.com</p>
          <p className="text-white/60 text-xs">Manawatu, New Zealand</p>
        </div>
      </div>
    </div>

    {/* Hero Section */}
    <div className="bg-gradient-to-r from-[#1a4d2e] to-[#2d6b45] text-white px-8 py-5">
      <h2 className="text-xl font-semibold mb-2">We Come to You. Quality Data. Every Time.</h2>
      <p className="text-white/90 text-sm leading-relaxed">
        Measured Livestock provides professional, on-farm livestock data services for dairy and beef operations. 
        We bring the expertise and equipment to your farm, saving you time and ensuring 
        <span className="font-semibold"> accurate, high-quality data </span> 
        for better breeding and management decisions.
      </p>
    </div>

    {/* Main Content */}
    <div className="flex-1 px-8 py-4">
      
      {/* Services Grid */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        
        {/* Service 1 */}
        <div className="bg-[#f0f7f2] rounded-lg p-4 border border-[#1a4d2e]/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">⚖️</span>
            <h3 className="font-bold text-[#1a4d2e] text-sm">Cattle Weighing</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Accurate weight recording linked to individual animal EIDs. Data formatted and ready for your management system.
          </p>
        </div>

        {/* Service 2 */}
        <div className="bg-[#f0f7f2] rounded-lg p-4 border border-[#1a4d2e]/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🧬</span>
            <h3 className="font-bold text-[#1a4d2e] text-sm">(DNA) Tissue Sampling</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Complete DNA tissue collection with accurate sample-to-animal matching. Ready for your chosen genomic provider.
          </p>
        </div>

        {/* Service 3 */}
        <div className="bg-[#f0f7f2] rounded-lg p-4 border border-[#1a4d2e]/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">✅</span>
            <h3 className="font-bold text-[#1a4d2e] text-sm">Herd Audit</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Physical verification against your records for NAIT, MINDA, and myHERD compliance. Keep your data accurate.
          </p>
        </div>

        {/* Service 4 */}
        <div className="bg-[#f0f7f2] rounded-lg p-4 border border-[#1a4d2e]/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📊</span>
            <h3 className="font-bold text-[#1a4d2e] text-sm">Special Projects</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Tailored field projects and custom data collection. Contact office to discuss requirements.
          </p>
        </div>

        {/* Service 5 - placeholder */}
        <div className="bg-[#f0f7f2] rounded-lg p-4 border border-[#1a4d2e]/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📞</span>
            <h3 className="font-bold text-[#1a4d2e] text-sm">Contact Us</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Have specific requirements? Get in touch to discuss your needs and we'll provide a custom quote.
          </p>
        </div>

        {/* Service 6 */}
        <div className="bg-[#f0f7f2] rounded-lg p-4 border border-[#1a4d2e]/20">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📁</span>
            <h3 className="font-bold text-[#1a4d2e] text-sm">Special Projects</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Custom data collection and field projects. Contact us to discuss your specific requirements.
          </p>
        </div>

      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6">
        
        {/* Left Column - Why Choose Us */}
        <div>
          <h3 className="font-bold text-[#1a4d2e] text-sm mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-[#1a4d2e] rounded-full flex items-center justify-center text-white text-xs">★</span>
            Why Choose Measured Livestock?
          </h3>
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <span className="text-[#1a4d2e] font-bold text-sm">✓</span>
              <div>
                <p className="text-xs font-semibold text-gray-800">We Come to You</p>
                <p className="text-xs text-gray-500">Professional service on your farm, on your schedule</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#1a4d2e] font-bold text-sm">✓</span>
              <div>
                <p className="text-xs font-semibold text-gray-800">Accurate Data, First Time</p>
                <p className="text-xs text-gray-500">Real-time validation reduces errors and rework</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#1a4d2e] font-bold text-sm">✓</span>
              <div>
                <p className="text-xs font-semibold text-gray-800">No Provider Lock-In</p>
                <p className="text-xs text-gray-500">Use any genomic provider or lab you choose</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#1a4d2e] font-bold text-sm">✓</span>
              <div>
                <p className="text-xs font-semibold text-gray-800">Save Time & Hassle</p>
                <p className="text-xs text-gray-500">Extra hands when you need them most</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-[#1a4d2e] font-bold text-sm">✓</span>
              <div>
                <p className="text-xs font-semibold text-gray-800">Your Data, Your Control</p>
                <p className="text-xs text-gray-500">We never claim ownership of your data</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - How It Works */}
        <div>
          <h3 className="font-bold text-[#1a4d2e] text-sm mb-3 flex items-center gap-2">
            <span className="w-6 h-6 bg-[#1a4d2e] rounded-full flex items-center justify-center text-white text-xs">?</span>
            How It Works
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">1</div>
              <div>
                <p className="text-xs font-semibold text-gray-800">Get Your Quote Online</p>
                <p className="text-xs text-gray-500">Use PriceHub to select services and see instant pricing</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">2</div>
              <div>
                <p className="text-xs font-semibold text-gray-800">Book Your Visit</p>
                <p className="text-xs text-gray-500">Choose a date that works for your schedule</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">3</div>
              <div>
                <p className="text-xs font-semibold text-gray-800">We Arrive Ready</p>
                <p className="text-xs text-gray-500">Our team brings all equipment and expertise</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">4</div>
              <div>
                <p className="text-xs font-semibold text-gray-800">Get Your Data</p>
                <p className="text-xs text-gray-500">Clean, accurate data delivered to you and your providers</p>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* CTA Banner */}
      <div className="mt-4 bg-gradient-to-r from-[#1a4d2e] to-[#2d6b45] rounded-lg p-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-sm">Ready to Get Started?</p>
            <p className="text-white/80 text-xs mt-1">
              Visit <span className="font-semibold">measuredlivestock.com</span> to get an instant quote with PriceHub
            </p>
          </div>
          <div className="bg-white text-[#1a4d2e] px-4 py-2 rounded-lg font-bold text-sm">
            Try PriceHub →
          </div>
        </div>
      </div>

    </div>

    {/* Footer */}
    <div className="bg-gray-100 px-8 py-4 mt-auto">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bold text-[#1a4d2e] text-sm">Get in Touch</p>
          <p className="text-gray-600 text-xs">We're here to help with your livestock data needs.</p>
        </div>
        <div className="text-right text-xs text-gray-600">
          <p className="font-semibold text-[#1a4d2e]">measuredlivestock.com</p>
          <p>info@measuredlivestock.co.nz</p>
          <p>Manawatu, New Zealand</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-300 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Professional · Reliable · Farmer-Focused
        </p>
        <p className="text-xs text-gray-400">
          © {new Date().getFullYear()} Measured Livestock
        </p>
      </div>
    </div>

  </div>
);

export default FarmerBreederCoverPage;
